import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectQueryParams } from 'src/app/app.selectors';

import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountVerificationApiService } from '../services/account-verification-api.service';
import * as AccountVerificationActions from './account-verification.actions';
import { selectAccountVerificationUsername } from './account-verification.selectors';

@Injectable()
export class AccountVerificationEffects {
   activateAccount$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AccountVerificationActions.pageEnter),
         withLatestFrom(this.store.pipe(select(selectQueryParams))),
         switchMap(([action, queryParams]) => {
            const apiCall = this.accountVerificationApiService.activateAccount(queryParams.token);
            return apiCall.pipe(
               map((result) => {
                  return AccountVerificationActions.activateAccountSuccess({
                     accountVerification: result,
                  });
               }),
               catchError((error) => {
                  return of(AccountVerificationActions.activateAccountFailure(error));
               })
            );
         })
      );
   });

   canAccountBeFinalized$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AccountVerificationActions.activateAccountSuccess),
         withLatestFrom(this.store.pipe(select(selectQueryParams))),
         switchMap(([action, queryParams]) => {
            const apiCall = this.accountVerificationApiService.canAccountBeFinalized(queryParams.token);
            return apiCall.pipe(
               map((result) => {
                  return AccountVerificationActions.retrievedCanAccountBeFinalizedSuccess({
                     canAccountBeFinalized: result,
                  });
               }),
               catchError((error) => {
                  return of(AccountVerificationActions.retrievedCanAccountBeFinalizedFailure(error));
               })
            );
         })
      );
   });

   savePassword$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AccountVerificationActions.savePassword),
         withLatestFrom(
            this.store.pipe(select(selectQueryParams)),
            this.store.pipe(select(selectAccountVerificationUsername))
         ),
         switchMap(([action, queryParams, username]) => {
            const apiCall = this.accountVerificationApiService.savePassword(
               action.accountFinalization,
               queryParams.token
            );
            return apiCall.pipe(
               tap(() => {
                  let redirectRoute = '/login';
                  if (username) {
                     redirectRoute = redirectRoute + '?username=' + username;
                  }

                  this.router.navigateByUrl(redirectRoute);
               }),
               map(() => {
                  return AccountVerificationActions.savePasswordSuccess();
               }),
               catchError((error) => {
                  return of(AccountVerificationActions.savePasswordFailure(error));
               })
            );
         })
      );
   });

   accountCreatedSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AccountVerificationActions.savePasswordSuccess),
            tap(() => {
               this.snackBar.open('Account password has been set!');
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private router: Router,
      private store: Store<AppState>,
      private accountVerificationApiService: AccountVerificationApiService,
      private snackBar: MatSnackBar
   ) {}
}
